import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import SpotifyEmbed from '../SpotifyEmbed';

const API = process.env.REACT_APP_API_URL || '';

function formatDuration(seconds) {
  if (!seconds) return '—';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function isrcYear(isrc) {
  const m = (isrc || '').replace(/[-\s]/g, '').match(/^[A-Z0-9]{5}(\d{2})\d{5}$/);
  if (!m) return null;
  const yy = parseInt(m[1], 10);
  return yy <= 49 ? 2000 + yy : 1900 + yy;
}

function DuplicateReview() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [listening, setListening] = useState({}); // `${pairId}-${side}` -> true

  const load = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/api/library/duplicates`, { params: { status: 'pending', limit: 100 } })
      .then(res => {
        setRows(res.data.rows);
        setTotal(res.data.total);
      })
      .catch(err => setError(err.response?.data?.error || err.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleMerge = async (candidate, keepId, dropId) => {
    setBusyId(candidate.id);
    try {
      await axios.post(`${API}/api/library/duplicates/${candidate.id}/merge`, { keepId, dropId });
      setRows(rs => rs.filter(r => r.id !== candidate.id));
      setTotal(t => t - 1);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setBusyId(null);
    }
  };

  const handleDismiss = async (candidate) => {
    setBusyId(candidate.id);
    try {
      await axios.post(`${API}/api/library/duplicates/${candidate.id}/dismiss`);
      setRows(rs => rs.filter(r => r.id !== candidate.id));
      setTotal(t => t - 1);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div>
      <Header>
        <div>
          <Title>Duplicate Review</Title>
          <Subtitle>
            {loading ? 'Loading...' : `${total.toLocaleString()} pending pair${total === 1 ? '' : 's'}`}
          </Subtitle>
        </div>
      </Header>

      <InfoBox>
        These pairs share a title and artist with durations within a couple of seconds of each other —
        a likely sign the same recording was issued under two different ISRCs. Pairs where the ISRCs were
        assigned in different years are resolved automatically (the earlier one is kept, per the ISRC
        standard's year encoding) — what's left here all shares the <em>same</em> assignment year, so it's
        genuinely ambiguous and needs a human call. Confirm by ear if you're unsure, then pick which ISRC
        to keep as canonical; the other becomes a tracked alias, so if it ever shows up on a future credit
        sheet the tool will flag it and point back to the correct one.
      </InfoBox>

      {error && <ErrorBox>{error}</ErrorBox>}

      {!loading && rows.length === 0 && (
        <EmptyState>No pending duplicates to review.</EmptyState>
      )}

      <Pairs>
        {rows.map(r => {
          const aLangs = r.a_languages || [];
          const bLangs = r.b_languages || [];
          const bothHaveLangs = aLangs.length > 0 && bLangs.length > 0;
          const noOverlap = bothHaveLangs && !aLangs.some(l => bLangs.includes(l));

          return (
          <PairCard key={r.id}>
            <PairTitle>{r.a_title}{r.a_version ? <VersionTag> · {r.a_version}</VersionTag> : null}</PairTitle>
            <PairArtist>{r.a_artist}</PairArtist>

            {noOverlap && (
              <LangWarning>
                Likely different language versions ({aLangs.join('/')} vs {bLangs.join('/')}) — same title text is common
                across markets. Confirm by ear before merging.
              </LangWarning>
            )}

            <Candidates>
              <Candidate>
                <CandTop>
                  <CandInfo>
                    <Mono>{r.a_isrc}</Mono>
                    <Meta>
                      {formatDuration(r.a_duration)} · {isrcYear(r.a_isrc) || '?'} · {r.a_release_count} release{r.a_release_count === 1 ? '' : 's'}
                      {aLangs.length > 0 && <> · <LangTag>{aLangs.join('/')}</LangTag></>}
                    </Meta>
                  </CandInfo>
                  <CandActions>
                    <ListenBtn onClick={() => setListening(l => ({ ...l, [`${r.id}-a`]: !l[`${r.id}-a`] }))}>
                      {listening[`${r.id}-a`] ? 'Hide' : 'Listen'}
                    </ListenBtn>
                    <KeepBtn
                      disabled={busyId === r.id}
                      onClick={() => handleMerge(r, r.a_id, r.b_id)}
                    >
                      Keep this ISRC
                    </KeepBtn>
                  </CandActions>
                </CandTop>
                {listening[`${r.id}-a`] && (
                  <EmbedSlot><SpotifyEmbed isrc={r.a_isrc} compact /></EmbedSlot>
                )}
              </Candidate>

              <Candidate>
                <CandTop>
                  <CandInfo>
                    <Mono>{r.b_isrc}</Mono>
                    <Meta>
                      {formatDuration(r.b_duration)} · {isrcYear(r.b_isrc) || '?'} · {r.b_release_count} release{r.b_release_count === 1 ? '' : 's'}
                      {bLangs.length > 0 && <> · <LangTag>{bLangs.join('/')}</LangTag></>}
                    </Meta>
                  </CandInfo>
                  <CandActions>
                    <ListenBtn onClick={() => setListening(l => ({ ...l, [`${r.id}-b`]: !l[`${r.id}-b`] }))}>
                      {listening[`${r.id}-b`] ? 'Hide' : 'Listen'}
                    </ListenBtn>
                    <KeepBtn
                      disabled={busyId === r.id}
                      onClick={() => handleMerge(r, r.b_id, r.a_id)}
                    >
                      Keep this ISRC
                    </KeepBtn>
                  </CandActions>
                </CandTop>
                {listening[`${r.id}-b`] && (
                  <EmbedSlot><SpotifyEmbed isrc={r.b_isrc} compact /></EmbedSlot>
                )}
              </Candidate>
            </Candidates>

            <DismissBtn disabled={busyId === r.id} onClick={() => handleDismiss(r)}>
              Not a duplicate — dismiss
            </DismissBtn>
          </PairCard>
          );
        })}
      </Pairs>
    </div>
  );
}

const Header = styled.div`
  margin-bottom: 14px;
`;

const Title = styled.h1`
  font-family: var(--font-display);
  font-weight: 600;
  font-size: 24px;
  letter-spacing: -0.3px;
  color: var(--text);
`;

const Subtitle = styled.div`
  font-size: 12px;
  color: var(--text-2);
  margin-top: 2px;
`;

const InfoBox = styled.div`
  background: var(--blue-dim);
  color: var(--blue-dark);
  font-size: 12px;
  padding: 12px 16px;
  border-radius: var(--radius);
  margin-bottom: 20px;
  line-height: 1.5;
`;

const Pairs = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 14px;
`;

const PairCard = styled.div`
  background: var(--bg-2);
  border-radius: var(--radius);
  padding: 16px;
  box-shadow: var(--shadow-sm);
`;

const PairTitle = styled.div`
  font-weight: 600;
  font-size: 14px;
  color: var(--text);
`;

const VersionTag = styled.span`
  font-weight: 600;
  color: var(--text-2);
`;

const PairArtist = styled.div`
  font-size: 12px;
  color: var(--text-2);
  margin-bottom: 12px;
`;

const LangWarning = styled.div`
  background: var(--amber-dim);
  color: var(--amber);
  font-size: 11px;
  line-height: 1.4;
  padding: 8px 10px;
  border-radius: var(--radius);
  margin-bottom: 10px;
`;

const LangTag = styled.span`
  font-family: var(--font-mono);
  font-weight: 600;
  color: var(--text-2);
`;

const Candidates = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 10px;
`;

const Candidate = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px 10px;
  background: var(--bg-3);
  border-radius: 8px;
`;

const CandTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
`;

const CandActions = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
`;

const CandInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const ListenBtn = styled.button`
  background: none;
  border: 1px solid var(--border);
  color: var(--text-2);
  font-size: 10.5px;
  font-weight: 600;
  padding: 6px 10px;
  border-radius: var(--radius);
  white-space: nowrap;
  transition: all 0.15s;
  &:hover { border-color: var(--accent-border); color: var(--accent-text); }
`;

const EmbedSlot = styled.div`
  padding-top: 2px;
`;

const Mono = styled.span`
  font-family: var(--font-mono);
  font-size: 12px;
  color: var(--text);
  font-weight: 600;
`;

const Meta = styled.span`
  font-size: 11px;
  color: var(--text-3);
`;

const KeepBtn = styled.button`
  background: var(--gradient-ocean);
  color: white;
  font-size: 11px;
  font-weight: 700;
  padding: 7px 12px;
  border-radius: var(--radius);
  white-space: nowrap;
  transition: opacity 0.15s;
  &:hover:not(:disabled) { opacity: 0.85; }
  &:disabled { opacity: 0.5; cursor: default; }
`;

const DismissBtn = styled.button`
  width: 100%;
  background: none;
  border: 1.5px solid var(--border);
  color: var(--text-2);
  font-size: 11px;
  font-weight: 700;
  padding: 7px;
  border-radius: var(--radius);
  transition: all 0.15s;
  &:hover:not(:disabled) {
    border-color: var(--red);
    color: var(--red);
  }
  &:disabled { opacity: 0.5; cursor: default; }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px;
  color: var(--text-3);
  font-size: 13px;
`;

const ErrorBox = styled.div`
  background: var(--red-dim);
  color: var(--red);
  padding: 10px 14px;
  border-radius: var(--radius);
  margin-bottom: 14px;
  font-size: 13px;
`;

export default DuplicateReview;
